/**
 * Un artefacto del workspace, para leerlo de verdad.
 *
 * Hasta el 2026-08-14 se leia en un `textarea` monoespaciado de 12 px sobre fondo negro,
 * apretado en una columna de un panel dentro de una pestaña. El relevamiento o el analisis que
 * escribe un agente son documentos de dos paginas con titulos y tablas: ahi adentro no se leen,
 * se descifran.
 *
 * La modal arranca **en lectura**, que es lo que se hace el 90% de las veces, con el markdown
 * renderizado. Editar es un modo, no el estado por defecto: hasta hoy era al reves y no habia
 * forma de solo mirar.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Eye, Pencil, Save, Trash2, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import Markdown from '@/components/Markdown';

export default function ArtifactDialog({
  open, onOpenChange, artifact, rutaRelativa, onSave, onDelete, saving,
}) {
  const [modo, setModo] = useState('leer');
  const [texto, setTexto] = useState('');
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    setTexto(artifact?.content || '');
    // Cada vez que se abre otro archivo se vuelve a lectura: abrir un documento en modo
    // edicion invita a tocarlo sin querer.
    setModo('leer');
  }, [artifact?.artifact_id, artifact?.content]);

  const sucio = texto !== (artifact?.content || '');

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      toast.error('El navegador no dejo copiar');
    }
  };

  const cerrar = (abierto) => {
    if (!abierto && sucio && !window.confirm('Hay cambios sin guardar. ¿Cerrar igual?')) return;
    onOpenChange(abierto);
  };

  if (!artifact) return null;

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      {/* Ancho de lectura de verdad y alto casi completo: un informe no entra en 448 px. */}
      <DialogContent
        className="max-w-5xl w-[92vw] h-[88vh] flex flex-col rounded-sm border-brand-border p-0 gap-0"
        data-testid="artifact-dialog"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-brand-border shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-sm font-mono font-medium truncate text-left">
                {rutaRelativa}
              </DialogTitle>
              <p className="text-[10px] text-zinc-400 mt-0.5">
                v{artifact.current_version || 1}
                {artifact.updated_at ? ` · ${String(artifact.updated_at).slice(0, 16).replace('T', ' ')}` : ''}
                {sucio ? ' · sin guardar' : ''}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className="flex rounded-sm border border-brand-border overflow-hidden mr-1">
                {[['leer', 'Leer', Eye], ['editar', 'Editar', Pencil]].map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setModo(id)}
                    data-testid={`artifact-modo-${id}`}
                    className={`px-2.5 py-1 text-[11px] flex items-center gap-1 transition-colors ${
                      modo === id ? 'bg-brand-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                    }`}
                  >
                    <Icon className="w-3 h-3" /> {label}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1" onClick={copiar} title="Copiar el markdown">
                {copiado ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
              </Button>
              <Button
                size="sm" variant="outline" className="h-7 text-[11px] gap-1"
                onClick={() => onSave?.(texto)} disabled={saving || !sucio}
                data-testid="artifact-guardar"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
              </Button>
              <Button
                size="sm" variant="ghost" className="h-7 text-[11px] gap-1 text-brand-danger hover:text-brand-danger"
                onClick={() => onDelete?.()}
                data-testid="artifact-borrar"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {modo === 'leer' ? (
            <div className="max-w-[72ch] mx-auto" data-testid="artifact-lectura">
              <Markdown>{texto}</Markdown>
            </div>
          ) : (
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              className="font-mono text-xs h-full min-h-[60vh] resize-none rounded-sm"
              data-testid="artifact-editor"
              spellCheck={false}
            />
          )}
        </div>

        {modo === 'editar' && (
          <div className="px-5 py-2 border-t border-brand-border text-[10px] text-zinc-400 shrink-0">
            Markdown. Los titulos con <code className="font-mono">#</code>, las tablas con{' '}
            <code className="font-mono">|</code>. Se ve como queda en la pestaña Leer.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
